import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import useApiData from '../../services/useApiData.js';
import api from '../../services/axiosInterceptor.js';
import SnackbarEx from '../Helpers/SnackbarEx.jsx';

const US_STATES = [
    { label: 'Alabama', code: 'AL' }, { label: 'Alaska', code: 'AK' },
    { label: 'Arizona', code: 'AZ' }, { label: 'Arkansas', code: 'AR' },
    { label: 'California', code: 'CA' }, { label: 'Colorado', code: 'CO' },
    { label: 'Connecticut', code: 'CT' }, { label: 'Delaware', code: 'DE' },
    { label: 'Florida', code: 'FL' }, { label: 'Georgia', code: 'GA' },
    { label: 'Hawaii', code: 'HI' }, { label: 'Idaho', code: 'ID' },
    { label: 'Illinois', code: 'IL' }, { label: 'Indiana', code: 'IN' },
    { label: 'Iowa', code: 'IA' }, { label: 'Kansas', code: 'KS' },
    { label: 'Kentucky', code: 'KY' }, { label: 'Louisiana', code: 'LA' },
    { label: 'Maine', code: 'ME' }, { label: 'Maryland', code: 'MD' },
    { label: 'Massachusetts', code: 'MA' }, { label: 'Michigan', code: 'MI' },
    { label: 'Minnesota', code: 'MN' }, { label: 'Mississippi', code: 'MS' },
    { label: 'Missouri', code: 'MO' }, { label: 'Montana', code: 'MT' },
    { label: 'Nebraska', code: 'NE' }, { label: 'Nevada', code: 'NV' },
    { label: 'New Hampshire', code: 'NH' }, { label: 'New Jersey', code: 'NJ' },
    { label: 'New Mexico', code: 'NM' }, { label: 'New York', code: 'NY' },
    { label: 'North Carolina', code: 'NC' }, { label: 'North Dakota', code: 'ND' },
    { label: 'Ohio', code: 'OH' }, { label: 'Oklahoma', code: 'OK' },
    { label: 'Oregon', code: 'OR' }, { label: 'Pennsylvania', code: 'PA' },
    { label: 'Rhode Island', code: 'RI' }, { label: 'South Carolina', code: 'SC' },
    { label: 'South Dakota', code: 'SD' }, { label: 'Tennessee', code: 'TN' },
    { label: 'Texas', code: 'TX' }, { label: 'Utah', code: 'UT' },
    { label: 'Vermont', code: 'VT' }, { label: 'Virginia', code: 'VA' },
    { label: 'Washington', code: 'WA' }, { label: 'West Virginia', code: 'WV' },
    { label: 'Wisconsin', code: 'WI' }, { label: 'Wyoming', code: 'WY' },
];

const inputProps = {
    size: 'small',
    variant: 'outlined',
    InputLabelProps: { shrink: true },
};

const EMPTY_FORM = {
    addressLine: '',
    city: '',
    stateProvinceCode: '',
    postalCode: '',
    countryCode: 'US',
};

export default function ShippingAddress() {
    const { data, load: isLoading, refetch } = useApiData('api/broylesllc/configuration/shipping-address');
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' });

    useEffect(() => {
        if (data) {
            setForm({
                addressLine: data.addressLine ?? '',
                city: data.city ?? '',
                stateProvinceCode: data.stateProvinceCode ?? '',
                postalCode: data.postalCode ?? '',
                countryCode: data.countryCode ?? 'US',
            });
        }
    }, [data]);

    const handleChange = (field) => (e) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const selectedState = US_STATES.find((s) => s.code === form.stateProvinceCode) ?? null;

    const isDirty = data && Object.keys(EMPTY_FORM).some(
        (k) => (data[k] ?? '') !== form[k]
    );

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/api/broylesllc/configuration/shipping-address', form);
            setSnackbar({ open: true, message: 'Shipping address saved.', color: 'success' });
            refetch();
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message ?? 'Failed to save shipping address.';
            setSnackbar({ open: true, message: msg, color: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center pt-5">
                <CircularProgress size={24} />
            </div>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-center align-items-center h-100 pt-0 pb-4">
                <Card variant="outlined" sx={{ maxWidth: 480, width: '100%', borderRadius: 3 }} elevation={0}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                            <PlaceOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                            <Typography variant="subtitle1" fontWeight={500}>
                                Ship-from address
                            </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                            Used on all UPS labels and rate quotes
                        </Typography>

                        <Divider sx={{ mb: 2.5 }} />

                        <Stack spacing={2}>
                            {/* Address Line */}
                            <TextField
                                {...inputProps}
                                label="Address line"
                                value={form.addressLine}
                                onChange={handleChange('addressLine')}
                                fullWidth
                            />

                            {/* City + State */}
                            <div className="row g-2">
                                <div className="col-8">
                                    <TextField
                                        {...inputProps}
                                        label="City"
                                        value={form.city}
                                        onChange={handleChange('city')}
                                        fullWidth
                                    />
                                </div>
                                <div className="col-4">
                                    <Autocomplete
                                        options={US_STATES}
                                        getOptionLabel={(o) => o.code}
                                        value={selectedState}
                                        onChange={(_, newVal) =>
                                            setForm((prev) => ({ ...prev, stateProvinceCode: newVal?.code ?? '' }))
                                        }
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.code}>
                                                <span style={{ fontWeight: 500, marginRight: 6 }}>{option.code}</span>
                                                <span style={{ color: '#667085', fontSize: 12 }}>{option.label}</span>
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                {...inputProps}
                                                label="State"
                                            />
                                        )}
                                        size="small"
                                        fullWidth
                                        disableClearable={!!selectedState}
                                    />
                                </div>
                            </div>

                            {/* Postal Code + Country */}
                            <div className="row g-2">
                                <div className="col-8">
                                    <TextField
                                        {...inputProps}
                                        label="Postal code"
                                        value={form.postalCode}
                                        onChange={handleChange('postalCode')}
                                        fullWidth
                                    />
                                </div>
                                <div className="col-4">
                                    <TextField
                                        {...inputProps}
                                        label="Country"
                                        value={form.countryCode}
                                        onChange={handleChange('countryCode')}
                                        fullWidth
                                    />
                                </div>
                            </div>

                            <Button
                                variant="contained"
                                disableElevation
                                disabled={saving || !isDirty}
                                onClick={handleSave}
                                fullWidth
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    backgroundColor: '#102A43',
                                    '&:hover': { backgroundColor: '#1a3d5c' },
                                }}
                            >
                                {saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Save address'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </div>

            <SnackbarEx
                open={snackbar.open}
                message={snackbar.message}
                color={snackbar.color}
                close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </>
    );
}
